// components/pricing/PricingHero.tsx
"use client";

import { useEffect, useRef } from "react";
import { useTranslations } from "next-intl";
import HexLive from "@/components/HexLive";

export default function PricingHero() {
  const t = useTranslations("pricing.hero");
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const children = el.querySelectorAll<HTMLElement>("[data-fade]");
    children.forEach((child, i) => {
      child.style.opacity = "0";
      child.style.transform = "translateY(24px)";
      child.style.transition = `opacity 0.7s ease ${i * 0.15}s, transform 0.7s ease ${i * 0.15}s`;
      requestAnimationFrame(() => {
        child.style.opacity = "1";
        child.style.transform = "translateY(0)";
      });
    });
  }, []);

  return (
    <section
      ref={containerRef}
      className="relative min-h-[50vh] flex items-center justify-center overflow-hidden
                 bg-base px-6 py-24 pt-36"
    >
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage: `
            linear-gradient(rgb(var(--accent) / 0.15) 1px, transparent 1px),
            linear-gradient(90deg, rgb(var(--accent) / 0.15) 1px, transparent 1px)
          `,
          backgroundSize: "60px 60px",
          maskImage: "radial-gradient(ellipse 80% 60% at 50% 40%, black, transparent 80%)",
          WebkitMaskImage: "radial-gradient(ellipse 80% 60% at 50% 40%, black, transparent 80%)",
        }}
      />
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: "radial-gradient(ellipse 70% 50% at 50% 60%, rgb(var(--accent) / 0.12) 0%, transparent 70%)",
        }}
      />

      <HexLive className="hidden md:block absolute right-[8%] top-24 w-20 h-20"  mode="pulse" baseOpacity={0.15} intervalSec={42} />
      <HexLive className="hidden md:block absolute right-[15%] bottom-12 w-32 h-32" mode="pulse" baseOpacity={0.10} intervalSec={55} />
      <HexLive className="hidden md:block absolute right-[5%] bottom-32 w-12 h-12"  mode="spark" baseOpacity={0.20} intervalSec={32} />
      <HexLive className="hidden md:block absolute left-[3%] bottom-20 w-16 h-16"  mode="spark" baseOpacity={0.12} intervalSec={38} />

      <div className="relative z-10 max-w-4xl text-center flex flex-col items-center gap-6">
        <span
          data-fade
          className="inline-flex items-center gap-2 rounded-full border border-accent-md
                     bg-accent-soft px-4 py-1.5 text-xs font-semibold uppercase tracking-widest
                     text-accent"
        >
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
          {t("badge")}
        </span>

        <h1
          data-fade
          className="text-4xl md:text-6xl font-black leading-[1.05] tracking-tight text-fore"
          style={{ textShadow: "0 0 40px rgb(var(--accent) / 0.2)" }}
        >
          {t("title")}
        </h1>

        <p
          data-fade
          className="max-w-2xl text-base md:text-lg text-sec leading-relaxed"
        >
          {t("subtitle")}
        </p>
      </div>
    </section>
  );
}

function HexDecor({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 100 115" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <polygon
        points="50,5 95,27.5 95,87.5 50,110 5,87.5 5,27.5"
        stroke="rgb(var(--accent))"
        strokeWidth="2"
        fill="none"
      />
    </svg>
  );
}